import type {
  PortalTherapistRole,
  DomainEntity,
  TherapistNotifications,
  TherapistQuestionnaireTriggerCondition,
} from "@akina/domain";
import { toCamelCase, toSnakeCase } from "@akina/utils";
import type {
  AccessibleTreatmentTemplatesResponse,
  AccessibleTreatmentTemplatesResponseApi,
  TermsAndConditionsRequest,
  TermsAndConditionsResponse,
  TermsAndConditionsResponseApi,
  TherapistFavouriteExercisesResponse,
  TherapistFavouriteExercisesResponseApi,
  TherapistFavouriteExercisesUpdateApi,
  TherapistSchemaRead,
  TherapistSchemaReadApi,
  TherapistUpdate,
  TherapistUpdateApi,
  TreatmentTemplateWithPersonalizationsResponse,
  TreatmentTemplateWithPersonalizationsResponseApi,
  UserExperienceEnum,
} from "types/schemas";
import { type AvailableLocales } from "../../locales";
import { ApiMethod, type ApiConfiguration } from "../shared";
import { uploadFileToS3 } from "../shared/uploadFileToS3";

const THERAPIST_EMAIL_NOTIFICATION_KEYS = [
  "openTaskReminders",
  "newPatientAssigned",
  "phaseAdvancing",
  "acceptsExtensionAndPrescriptionReminders",
] as const satisfies readonly (keyof TherapistNotifications)[];

function isCompleteTherapistEmailNotifications(
  email: Partial<TherapistNotifications> | null | undefined,
): email is TherapistNotifications {
  if (!email) {
    return false;
  }
  return THERAPIST_EMAIL_NOTIFICATION_KEYS.every(key => typeof email[key] === "boolean");
}

function therapistUpdatePreferencesNotificationsFromRead(read: TherapistSchemaRead): {
  notifications: TherapistUpdate["preferences"]["notifications"];
} | Record<string, never> {
  const emailPrefs = read.preferences?.notifications?.email;
  if (emailPrefs == null) {
    return { notifications: null };
  }
  if (isCompleteTherapistEmailNotifications(emailPrefs)) {
    return { notifications: { email: emailPrefs } };
  }
  const hasAnyDefinedBoolean = THERAPIST_EMAIL_NOTIFICATION_KEYS.some(key => typeof emailPrefs[key] === "boolean");
  if (!hasAnyDefinedBoolean) {
    return { notifications: null };
  }
  return {};
}

function therapistSchemaReadToTherapistUpdate(read: TherapistSchemaRead): TherapistUpdate {
  return {
    firstName: read.firstName,
    lastName: read.lastName,
    profilePictureUrl: read.profilePictureUrl ?? null,
    subtitle: read.subtitle ?? null,
    description: read.description ?? null,
    preferences: {
      language: read.preferences?.language ?? null,
      ...therapistUpdatePreferencesNotificationsFromRead(read),
    },
  };
}

export type InvitePatientFormValues = {
  bodyPart: string;
  firstName: string;
  lastName: string;
  email: string;
  therapistId: string;
  organizationId: string;
  treatmentTemplateKey: string;
  language: (typeof AvailableLocales)[number];
  prescription?: File;
  dateOfBirth: Date;
  treatmentPersonalizationId?: string;
  isSelfPayer: boolean;
} & DomainEntity;

export const invitePatientMapper = {
  toDTO: (invitePatientDomain: InvitePatientFormValues) => ({
    first_name: invitePatientDomain.firstName,
    last_name: invitePatientDomain.lastName,
    email: invitePatientDomain.email,
    therapist_id: invitePatientDomain.therapistId,
    organization_id: invitePatientDomain.organizationId,
    treatment_template_key: invitePatientDomain.treatmentTemplateKey,
    language: invitePatientDomain.language,
    prescription_mime_type: invitePatientDomain?.prescription?.type,
    date_of_birth: invitePatientDomain.dateOfBirth,
    treatment_personalization_id: invitePatientDomain.treatmentPersonalizationId || undefined,
    is_self_payer: invitePatientDomain.isSelfPayer,
  }),
};

export const therapistApi = (config: ApiConfiguration) => ({
  async getById(id: string): Promise<TherapistSchemaRead | null> {
    const result = await config.httpService.fetch<TherapistSchemaReadApi | null>({
      method: ApiMethod.GET,
      url: `${config.endpoints.therapists.get}${id}/`,
    });

    if (!result) return null;

    return toCamelCase(result);
  },
  async getMy(signal?: AbortSignal): Promise<TherapistSchemaRead> {
    const result = await config.httpService.fetch<TherapistSchemaReadApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.myTherapist,
      signal,
    });

    return toCamelCase(result);
  },

  async update(therapist: TherapistSchemaRead, signal?: AbortSignal) {
    const payload = therapistSchemaReadToTherapistUpdate(therapist);
    await config.httpService.fetch<TherapistUpdateApi>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.myTherapist,
      data: toSnakeCase(payload),
      signal,
    });
  },
  async updateProfilePicture(newProfilePicture: File): Promise<TherapistSchemaRead> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.profilePicture.uploadUrl,
      data: { file_type: newProfilePicture.type },
    });

    if (!result?.presigned_url) {
      throw new Error("Profile picture could not be uploaded; please try again.");
    }
    await uploadFileToS3(result.presigned_url, newProfilePicture);

    return await this.getMy();
  },
  async deleteProfilePicture(): Promise<TherapistSchemaRead> {
    await config.httpService.fetch<string>({
      method: ApiMethod.DELETE,
      url: config.endpoints.patients.profilePicture.delete,
    });

    return await this.getMy();
  },
  async getFavouriteExercises(signal?: AbortSignal): Promise<TherapistFavouriteExercisesResponse> {
    const response = await config.httpService.fetch<TherapistFavouriteExercisesResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.getFavouriteExercises,
      signal,
    });
    return toCamelCase(response);
  },

  async patchFavouriteExercises(exerciseKeys: string[], signal?: AbortSignal) {
    await config.httpService.fetch<TherapistFavouriteExercisesUpdateApi>({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.getFavouriteExercises,
      data: { favourite_exercises: exerciseKeys },
      signal,
    });
  },

  async getTermsAcceptedStatus(): Promise<TermsAndConditionsResponse> {
    const response = await config.httpService.fetch<TermsAndConditionsResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.legalTerms.get("therapists"),
    });
    return toCamelCase(response);
  },

  async updateTermsAcceptedStatus(status: TermsAndConditionsRequest): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.legalTerms.accept("therapists"),
      data: toSnakeCase(status),
    });
  },
  async invitePatient(
    invitationDetails: InvitePatientFormValues,
  ): Promise<void | { prescriptionUploadSkipped: true }> {
    const response = await config.httpService.fetch<{
      presigned_url: string | null;
      object_name: string | null;
      treatment_id: string;
    }>({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.invitePatient,
      data: invitePatientMapper.toDTO(invitationDetails),
    });

    const prescription = invitationDetails?.prescription;
    if (prescription) {
      if (!response?.presigned_url || !response?.object_name) {
        return { prescriptionUploadSkipped: true };
      }
      await uploadFileToS3(response.presigned_url, prescription);
      await config.httpService.fetch({
        method: ApiMethod.PATCH,
        url: config.endpoints.treatments.prescriptionUploadSuccess,
        data: {
          treatment_id: response.treatment_id,
          prescription_object_name: response.object_name,
        },
      });
    }
  },
  async delete(params: { therapistId: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.therapists.delete(params.therapistId),
    });
    return params.therapistId;
  },
  async editRole(params: { therapistId: string; role: PortalTherapistRole }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.editRole,
      data: {
        therapist_id: params.therapistId,
        role: params.role,
      },
    });
  },

  async addDevice(params: { token: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.devices.register,
      data: {
        token: params.token,
      },
    });
  },

  async removeDevice(params: { token: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.therapists.devices.unregister,
      data: {
        token: params.token,
      },
    });
  },
  async listDevices(): Promise<string[]> {
    return await config.httpService.fetch<string[]>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.devices.list,
    });
  },
  async sendReminder(params: { treatmentId: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.sendReminder,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async resendInvite(params: { treatmentId?: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.resendInvite,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async revokeInvite(params: { treatmentId?: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.therapists.revokeInvite,
      data: {
        treatment_id: params.treatmentId,
      },
    });
  },
  async submitHappinessFeedback({
    rating,
    comments,
    triggerCondition,
  }: {
    rating: UserExperienceEnum;
    comments: string;
    triggerCondition: TherapistQuestionnaireTriggerCondition;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.therapists.happinessFeedback,
      data: {
        trigger_condition: triggerCondition,
        user_experience: rating,
        comments: comments,
      },
    });
  },
  async getDisplayHappinessQuestionnaire(params: {
    therapistId: string;
  }): Promise<{ triggerQuestionnaire: boolean; triggerCondition: TherapistQuestionnaireTriggerCondition }> {
    const result = await config.httpService.fetch<{
      trigger_questionnaire: boolean;
      trigger_condition: TherapistQuestionnaireTriggerCondition;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.displayHappinessQuestionnaire(params.therapistId),
    });

    return {
      triggerQuestionnaire: result.trigger_questionnaire,
      triggerCondition: result.trigger_condition,
    };
  },
  async getTreatmentTemplates(therapistId: string): Promise<AccessibleTreatmentTemplatesResponse> {
    const result = await config.httpService.fetch<AccessibleTreatmentTemplatesResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.getTreatmentTemplates(therapistId),
    });

    return toCamelCase(result);
  },
  async getTreatmentTemplateWithPersonalizations(
    therapistId: string,
    treatmentTemplateKey: string,
  ): Promise<TreatmentTemplateWithPersonalizationsResponse> {
    const result = await config.httpService.fetch<TreatmentTemplateWithPersonalizationsResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.therapists.getTreatmentTemplateWithPersonalizations(therapistId, treatmentTemplateKey),
    });

    return toCamelCase(result);
  },
});

export * from "./useDeleteProfilePicture";
export * from "./useDeleteTherapist";
export * from "./useEditTherapistRole";
export * from "./useFetchMyTherapist";
export * from "./useFetchTherapistsForClinic";
export * from "./useResendInvite";
export * from "./useRevokeInvite";
export * from "./useSendReminder";
export * from "./useUpdateMyTherapist";
export * from "./useUpdateProfilePicture";
export * from "./useFetchFavouriteExercises";
export * from "./usePatchFavouriteExercises";
